import { cva, cx } from 'class-variance-authority';

import { colorMap } from '@constants/colors';

import Actions from '@components/Actions';
import Frame from '@components/Frame';
import Illustration from '@components/Illustration';
import Image from '@components/Image';
import TextRenderer from '@components/TextRenderer';
import TickList from '@components/TickList';
import SanityImage from '@components/SanityImage';

import flowStyles from '@styles/utilities/flow.module.css';
import textStyles from '@styles/textStyles.module.css';
import measureStyles from '@styles/utilities/measures.module.css';

import * as styles from './MediaWithText.module.css';
import Explainer from '@components/Explainer';

const rootVariants = cva(styles.root, {
  variants: {
    theme: {
      light: styles.lightTheme,
      dark: styles.darkTheme,
      accent: styles.accentTheme,
    },
    bleed: {
      true: styles.bleed,
    },
    align: {
      right: styles.alignRight,
    },
    hasMediaAspectRatio: {
      true: styles.hasMediaAspectRatio,
    },
    sectionWidth: {
      default: styles.defaultSectionWidth,
      small: styles.smallSectionWidth,
    },
  },
});

const textVariants = cva([flowStyles.root, flowStyles.small], {
  variants: {
    textSize: {
      small: [textStyles.bodyS, measureStyles.mediumWide],
      medium: [textStyles.bodyM, measureStyles.medium],
      large: [textStyles.bodyL, measureStyles.narrow],
    },
  },
});

const BaseMediaWithText = ({
  align = 'left',
  actions,
  bleed = true,
  text,
  textSize = 'medium',
  listItems,
  eyebrow,
  title,
  markerColor,
  markerColorEnd,
  media,
  mediaAspectRatio = '16 / 9',
  mediaMaxHeight = '600px',
  renderAs = 'div',
  sectionWidth = 'default',
  theme = 'light',
  style,
  className,
}) => {
  const cssClasses = rootVariants(
    {
      theme,
      bleed,
      align,
      sectionWidth,
      hasMediaAspectRatio: !!mediaAspectRatio,
    },
    className,
  );

  const textClasses = textVariants({ textSize });

  const Tag = renderAs;
  const hasMedia = !!media;

  // This is a hack because we used to have just eyebrow, then changed it to look like
  // a heading, and then introduced title. So many Sanity blocks have just eyebrow that
  // needs to be rendered like the heading. If only eyebrow is set, we render it like a heading.
  const renderedEyebrow = eyebrow && !title ? null : eyebrow;
  const renderedTitle = eyebrow && !title ? eyebrow : title;

  return (
    <Tag
      className={cssClasses}
      style={{
        '--marker-color': markerColor && colorMap[markerColor],
        '--marker-color-end': markerColorEnd && colorMap[markerColorEnd],
        '--media-max-height': mediaMaxHeight,
        '--media-aspect-ratio': mediaAspectRatio,
        ...style,
      }}
    >
      <div className={styles.text}>
        {renderedEyebrow && (
          <p className={cx(textStyles.eyebrow, styles.eyebrow)}>
            {renderedEyebrow}
          </p>
        )}
        {renderedTitle && (
          <TextRenderer
            className={cx(textStyles.headlineS, styles.title)}
            text={renderedTitle}
            as="h2"
          />
        )}
        {text && <TextRenderer className={textClasses} text={text} />}

        {listItems && <TickList className={styles.list} items={listItems} />}

        {actions && (
          <Actions className={styles.actions} actions={actions} theme={theme} />
        )}
      </div>
      <div className={styles.media} aria-hidden={!hasMedia || null}>
        {media}
      </div>
    </Tag>
  );
};

export const IllustrationWithText = ({ illustration, ...rest }) => {
  return (
    <BaseMediaWithText
      {...rest}
      media={<Illustration illustration={illustration} />}
    />
  );
};

export const ImageWithText = ({ image, ...rest }) => {
  return (
    <BaseMediaWithText
      {...rest}
      media={
        <div>
          {image ? (
            image.src ? (
              <Image
                fill
                src={image.src}
                alt={image.alt}
                className={styles.image}
                sizes="(min-width: 1200px) 50vw, 100vw"
              />
            ) : (
              <SanityImage
                fill
                asset={image.image.asset}
                alt={image.alt}
                className={styles.image}
                sizes="(min-width: 1200px) 50vw, 100vw"
              />
            )
          ) : null}
        </div>
      }
    />
  );
};

export const FrameWithText = ({ image, ...rest }) => {
  return (
    <BaseMediaWithText
      {...rest}
      media={
        <Frame
          size="small"
          theme={image.background ?? 'light'}
          gradient={image.gradient}
          invertGradient={image.invertGradient}
        >
          {image.src != null ? (
            <Image
              fill
              src={image.src}
              alt={image.alt}
              className={styles.image}
            />
          ) : (
            <SanityImage
              fill
              asset={image.image.asset}
              alt={image.alt}
              className={styles.image}
            />
          )}
        </Frame>
      }
    />
  );
};

export const ExplainerWithText = ({ explainer, ...rest }) => {
  return (
    <BaseMediaWithText
      {...rest}
      mediaAspectRatio={null}
      mediaMaxHeight={null}
      media={<Explainer explainer={explainer} />}
    />
  );
};
